// src/taskpane/modules/store.ts
/* global Office, localStorage, window, fetch, console, document */
import { LEGAL_DICT } from "./i18n_legal";
// --- 1. Types ---
export interface ModelProfile { id: string; name: string; url: string; model: string; apiKey: string; }
export interface ChatMessage { role: "system" | "user" | "assistant"; content: string; }
export interface ContextData { text: string; breadcrumbHtml: string; isHeading: boolean; fullPath?: string; rawText?: string; fullText?: string; }
export interface EngineConfig { url: string; onThought: (text: string) => void; onContent: (text: string) => void; onTransition: (state: "thinking" | "writing") => void; }

// --- 2. Config & Constants ---
const isLocal = typeof window !== 'undefined' && 
                (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

export const BACKEND_URL = isLocal 
    ? "https://localhost:8788" 
    : "https://bidarch.hans3n.top";

export const BASE_KEYS = { REDBOOK: "bid_draft_redbook", RULES: "bid_draft_rules" };

export const getPromptModes = (lang: "en" | "zh") => [
    { value: "generate", label: I18N_DICT[lang]["prompt-label-generate"] },
    { value: "outline",  label: I18N_DICT[lang]["prompt-label-outline"] },
    { value: "polish",   label: I18N_DICT[lang]["prompt-label-polish"] },
    { value: "check",    label: I18N_DICT[lang]["prompt-label-check"] },
    { value: "revise",   label: I18N_DICT[lang]["prompt-label-revise"] },
    { value: "refine",   label: I18N_DICT[lang]["prompt-label-refine"] }
];

export const DOM = {
    // API & Profiles
    INPUT_NAME: "profileName", INPUT_URL: "apiUrl", INPUT_MODEL: "modelName", INPUT_KEY: "apiKey",
    BTN_CANCEL_EDIT: "btn-cancel-edit", BTN_ADD_PROFILE: "btn-add-profile", BTN_TEST: "btn-test-api",
    LIST_Container: "profileList", FORM_Container: "profileForm", HIDDEN_ID: "editingProfileId",
    DROPDOWN_WRAPPER: "modelDropdown", DROPDOWN_TRIGGER: "dropdownTrigger", DROPDOWN_MENU: "dropdownMenu", CURRENT_MODEL_TEXT: "currentModelText",
    AREA_API: "apiArea", API_FEEDBACK: "apiFeedback", 
    // Task Areas
    GLOBAL_RULES: "globalRules", RED_BOOK: "redBook", FINAL_OUTPUT: "finalOutput", BRAIN_OUTPUT: "brainOutput",
    BTN_REFINE_REDBOOK: "btn-refine-redbook", BTN_SAVE_TASK: "btn-save-task", BTN_CANCEL_TASK: "btn-cancel-task",
    BREADCRUMB: "breadcrumb", STATUS_BAR: "status", TASK_FEEDBACK: "taskFeedback", AREA_TASK: "taskArea",
    TAB_PREVIEW: "tab-preview", TAB_BRAIN: "tab-brain",
    // Task Actions
    BTN_GENERATE: "btn-generate", BTN_OUTLINE: "btn-outline", BTN_POLISH: "btn-polish", BTN_CHECK: "btn-check", 
    BTN_INSERT: "btn-insert", ROW_REVISE_INPUT: "revise-input-row", INSTRUCTION: "instruction", BTN_SUBMIT_REVISE: "btn-submit-revise", 
    // Settings & Prompts
    BTN_OPEN_SETTINGS: "btn-open-settings", BTN_CLOSE_SETTINGS: "btn-close-settings", SETTINGS_OVERLAY: "settings-overlay", BTN_CLEAN_DOC: "btn-clean-doc",
    AREA_PROMPT: "promptSettingsArea", PROMPT_SELECTOR: "promptSelector", PROMPT_EDITOR: "promptEditor", 
    BTN_SAVE_PROMPT: "btn-save-prompt", BTN_RESET_PROMPT: "btn-reset-prompt", PROMPT_FEEDBACK: "promptFeedback", LIST_PROMPT_CONTAINER: "promptListContainer",
    // Debug & Logs
    BTN_COPY_LOGS: "btn-copy-logs"
};

export const ICONS = {
    SUCCESS: `<svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/></svg>`,
    ERROR: `<svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor"><path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/></svg>`,
    INSERT: `<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" style="margin-right:6px;"><path d="M8 2a.5.5 0 0 1 .5.5v8.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1-.708-.708L7.5 11.293V2.5A.5.5 0 0 1 8 2z"/></svg>`,
    CHEVRON: `<svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" style="opacity:0.4; margin:0 1px;"><path d="M5.646 2.646a.5.5 0 0 1 .708 0l5 5a.5.5 0 0 1 0 .708l-5 5a.5.5 0 0 1-.708-.708L10.293 8 5.646 3.354a.5.5 0 0 1 0-.708z"/></svg>`,
    BROOM: `<svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor"><path d="M6.5 1A1.5 1.5 0 0 0 5 2.5V3H1.5A1.5 1.5 0 0 0 0 4.5v1.384l7.614 2.03a1.5 1.5 0 0 0 .772 0L16 5.884V4.5A1.5 1.5 0 0 0 14.5 3H11v-.5A1.5 1.5 0 0 0 9.5 1h-3zm0 1h3a.5.5 0 0 1 .5.5V3H6v-.5a.5.5 0 0 1 .5-.5z"/><path d="M0 12.5A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5V6.85L8.129 8.947a.5.5 0 0 1-.258 0L0 6.85v5.65z"/></svg>`,
    STRUCTURE: `<svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M2 2a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V2zm0 5a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7zm0 5a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-2z"/></svg>`,
    STOP: `<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="2" y="2" width="12" height="12" rx="2"/></svg>`
};

// --- 3. Internationalization (i18n) 全量字典 ---
export const I18N_DICT: Record<string, Record<string, string>> = {
    "en": {
        // --- Header & Global ---
        "title-settings": "System Settings",
        "title-clean-doc": "Clean Document (Remove AI Traces)",
        "currentModelText": "DeepBid Demo",
        "title-model-select": "Switch Model",
        
        // --- Toolbar ---
        "title-location": "Current Position",
        "breadcrumb-ready": "Ready...",
        "btn-generate": "Generate",
        "btn-outline": "Outline",
        "title-outline": "Generate sub-outline for current heading",
        "btn-polish": "Polish",
        "btn-check": "Check",
        
        // --- Tabs ---
        "tab-preview": "Preview",
        "tab-brain": "Thinking",
        "placeholder-preview": "Content preview area...",
        "placeholder-brain": "Thinking path...",
        
        // --- Footer & Input ---
        "instruction": "Enter revision instructions (Ctrl+Enter)...",
        "title-submit-revise": "Submit Revision",
        "btn-insert": "Insert Content",
        "btn-insert-outline": "Insert Outline",
        "ai-disclaimer": "AI content may be inaccurate, please verify before use!",
        "status-ready": "Ready",
        "status-thinking": "Thinking...",
        "status-writing": "Writing...",
        "status-waiting": "Waiting...",

        // --- Settings Modal ---
        "settings-title": "System Settings",
        "section-models": "Model Configuration",
        "section-prompts": "Prompt Configuration",
        "section-tasks": "Task Configuration",
        "toggle-expand": "Expand",
        "toggle-collapse": "Collapse",
        "btn-add-profile": "+ New",
        "label-name": "Name:",
        "label-url": "URL:",
        "label-model": "Model:",
        "label-key": "Key:",
        "btn-test-api": "Test & Save",
        "btn-cancel-edit": "Cancel",
        "btn-del-profile": "Delete Profile",
        "label-prompt-template": "Prompt Template:",
        "placeholder-prompt-editor": "Edit prompt for current mode...",
        "btn-save-prompt": "Save Prompt",
        "btn-reset-prompt": "Reset Default",
        "label-redbook": "Project Info (RedBook):",
        "btn-refine-redbook": "AI Refine",
        "title-refine": "Use AI to extract key info",
        "redBook": "Paste or refine RFP core info here...",
        "label-rules": "Specific Rules (Rules):",
        "globalRules": "e.g., Must mention CMMI5 certification...",
        "btn-save-task": "Save Config",
        "btn-cancel-task": "Cancel Modifications",
        "btn-copy-logs": "Copy Debug Logs",
        "status-select-content": "Please select body text first",
        "status-select-heading": "Please select a Heading first",
        "status-fetching": "Fetching context...",
        "status-insert-done": "Inserted successfully",
        "status-insert-fail": "Insert failed: ",
        "err-abort": "Operation cancelled",
        "err-rollback": "Request failed, original draft restored",
        "prompt-label-generate": "Generate",
        "prompt-label-outline": "Outline",
        "prompt-label-polish": "Polish",
        "prompt-label-check": "Check",
        "prompt-label-revise": "Revise",
        "prompt-label-refine": "Refine",
        "status-refining": "AI Refining...",
        "status-refine-done": "Refining completed",
        "status-verified": "Verified successfully",
        "status-missing-key": "Missing API Key",
        "status-cleaning": "Cleaning...",
        "status-clean-done": "Cleaned successfully",
        "btn-clean-confirm": "Confirm Clean",
        "profile-default-name": "New Profile",
        "status-loading": "Connecting...",
        "status-logs-copied": "Logs copied",
        "status-no-model": "No Model Configured",
        "Outline Preview": "Outline Preview",
        "status-using": "Active",
        ...LEGAL_DICT.en
    },
    "zh": {
        // --- 头部与全局 ---
        "title-settings": "系统设置",
        "title-clean-doc": "净化文档 (移除 AI 痕迹)",
        "currentModelText": "DeepSeek-V3",
        "title-model-select": "切换模型",
        
        // --- 工具栏 ---
        "title-location": "当前位置",
        "breadcrumb-ready": "准备就绪...",
        "btn-generate": "生成",
        "btn-outline": "大纲",
        "title-outline": "为当前标题生成子大纲",
        "btn-polish": "润色",
        "btn-check": "检查",
        
        // --- 标签页 ---
        "tab-preview": "正文预览",
        "tab-brain": "思考过程",
        "placeholder-preview": "内容预览区...",
        "placeholder-brain": "思考路径...",
        
        // --- 底部与输入 ---
        "instruction": "输入修订指令 (Ctrl/Cmd+Enter 发送)...",
        "title-submit-revise": "提交修订",
        "btn-insert": "插入正文",
        "btn-insert-outline": "插入大纲",
        "ai-disclaimer": "AI 生成内容未必正确，请人工核验后使用!",
        "status-ready": "准备就绪",
        "status-thinking": "思考中...",
        "status-writing": "撰写中...",
        "status-waiting": "等待操作...",

        // --- 设置弹窗 ---
        "settings-title": "系统设置",
        "section-models": "模型配置",
        "section-prompts": "指令配置",
        "section-tasks": "任务配置",
        "toggle-expand": "展开",
        "toggle-collapse": "收起",
        "btn-add-profile": "+ 新增",
        "label-name": "名称:",
        "label-url": "URL:",
        "label-model": "Model:",
        "label-key": "Key:",
        "btn-test-api": "测试并保存",
        "btn-cancel-edit": "取消",
        "btn-del-profile": "删除配置",
        "label-prompt-template": "提示词模板 (Prompt):",
        "placeholder-prompt-editor": "在此编辑当前模式的提示词...",
        "btn-save-prompt": "保存当前指令",
        "btn-reset-prompt": "恢复默认",
        "label-redbook": "项目信息 (RedBook):",
        "btn-refine-redbook": "智能提炼",
        "title-refine": "利用 AI 自动提取关键信息",
        "redBook": "在此粘贴或提炼招标文件核心信息...",
        "label-rules": "专属约束 (Rules):",
        "globalRules": "例如：必须提及CMMI5认证或所投硬件型号...",
        "btn-save-task": "保存配置",
        "btn-cancel-task": "取消修改",
        "btn-copy-logs": "复制调试日志",
        "status-select-content": "请先选中正文内容",
        "status-select-heading": "请先选中一个标题",
        "status-fetching": "自动获取段落内容...",
        "status-insert-done": "插入成功",
        "status-insert-fail": "插入失败: ",
        "err-abort": "已取消操作",
        "err-rollback": "请求失败，已恢复原草稿",
        "prompt-label-generate": "生成",
        "prompt-label-outline": "大纲",
        "prompt-label-polish": "润色",
        "prompt-label-check": "检查",
        "prompt-label-revise": "修订",
        "prompt-label-refine": "提炼",
        "status-refining": "AI 提炼中...",
        "status-refine-done": "提炼完成",
        "status-verified": "验证通过",
        "status-missing-key": "缺 API Key",
        "status-cleaning": "净化中...",
        "status-clean-done": "净化成功",
        "btn-clean-confirm": "再次点击删除",
        "profile-default-name": "新模型配置",
        "status-loading": "连接中...",
        "status-logs-copied": "日志已复制",
        "status-no-model": "未配置模型",
        "Outline Preview": "大纲预览",
        "status-using": "正在使用",
        ...LEGAL_DICT.zh
    }
};

/**
 * 优雅重构版：应用 UI 语言切换
 * 修复类型报错：将 Element 断言为 HTMLElement 以访问 innerText
 */
export function applyLanguageUI(lang: "en" | "zh") {
    const dict = I18N_DICT[lang];
    if (!dict) return;

    const elements = document.querySelectorAll("[data-i18n]");
    elements.forEach(node => {
        const el = node as HTMLElement;
        const key = el.getAttribute("data-i18n");
        if (!key || !dict[key]) return;

        // --- 核心保护逻辑 ---
        const hasSvg = el.querySelector("svg"); // 依然保留：用于判断是否包含图标
        const uiSpan = el.querySelector("span"); // 重命名变量，解决 TS2451 报错

        if (uiSpan) {
            uiSpan.innerText = dict[key]; // 如果有 span，只改 span 里的文字
        } else if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
            el.placeholder = dict[key]; // 处理输入框
        } else if (!hasSvg) {
            el.innerText = dict[key]; // 只有既没 span 又没 SVG 时，才直接改 innerText
        }

        // --- 状态缓存同步逻辑 ---
        if (el.dataset._orig) {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = el.dataset._orig;
            const cacheSpan = tempDiv.querySelector("span"); // 重命名变量，解决冲突
            if (cacheSpan) {
                cacheSpan.innerText = dict[key];
                el.dataset._orig = tempDiv.innerHTML;
            } else if (!tempDiv.querySelector("svg")) {
                tempDiv.innerText = dict[key];
                el.dataset._orig = tempDiv.innerHTML;
            }
        }
    });

    // 处理悬浮提示 (Title)
    const titleElements = document.querySelectorAll("[data-i18n-title]");
    titleElements.forEach(node => {
        const el = node as HTMLElement;
        const key = el.getAttribute("data-i18n-title");
        if (key && dict[key]) {
            el.setAttribute("title", dict[key]);
        }
    });
}

// --- 4. Managers ---
export const ProfileManager = {
    KEY_LOCAL: "bid_profiles", 
    KEY_ROAMING: "bid_profiles_roaming",

    save(profiles: ModelProfile[]) {
        const dataStr = JSON.stringify(profiles);
        // 1. 写入本地
        try { 
            localStorage.setItem(this.KEY_LOCAL, dataStr); 
        } catch (e) { console.error("Local save failed", e); }

        // 2. 写入漫游设置 (云端同步)
        try { 
            if (Office.context.roamingSettings) { 
                Office.context.roamingSettings.set(this.KEY_ROAMING, dataStr); 
                // 必须调用 saveAsync 才会真正发起同步
                Office.context.roamingSettings.saveAsync((result) => {
                    if (result.status === Office.AsyncResultStatus.Failed) {
                        console.warn("Roaming sync failed:", result.error.message);
                    }
                }); 
            } 
        } catch (e) { console.error("Roaming save failed", e); }
    },

    load(): ModelProfile[] {
        let finalData: string | null = null;

        // 优先尝试从漫游设置读取 (云端最新)
        try { 
            if (Office.context.roamingSettings) { 
                finalData = Office.context.roamingSettings.get(this.KEY_ROAMING); 
            } 
        } catch (e) { console.warn("Roaming read failed", e); }

        // 如果云端没数据，再降级读本地
        if (!finalData) {
            finalData = localStorage.getItem(this.KEY_LOCAL);
        } else {
            // [优化点] 如果读到了云端数据，顺手更新一下本地，保持双轨数据强一致
            try { localStorage.setItem(this.KEY_LOCAL, finalData); } catch(e) {}
        }

        return finalData ? JSON.parse(finalData) : [];
    }
};

let remotePrompts: Record<string, string> = {};
let remoteTemperatures: Record<string, number> = { generate: 0.7, polish: 0.7, check: 0.1, revise: 0.5, refine: 0.0, outline: 0.7, default: 0.5 };
let isSynced = false;

export const AppSettings = {
    async sync(lang: string = "zh"): Promise<{ models: ModelProfile[] | null, reviewMode: boolean }> {
        try {
            const url = `${BACKEND_URL}/api/defaults?lang=${lang}`; 
            const res = await fetch(url);
            if (!res.ok) throw new Error("Sync failed");
            const data = await res.json();
            
            if (data.prompts) remotePrompts = data.prompts;
            return { 
                models: data.models || null, 
                reviewMode: !!data.reviewMode 
            };
        } catch (e) { 
            return { models: null, reviewMode: false };
        }
    },
    getPrompt(mode: string): string { return localStorage.getItem(`bid_prompt_${mode}`) || remotePrompts[mode] || ""; },
    getTemp(mode: string): number { return remoteTemperatures[mode] ?? 0.5; },
    hasOverride(mode: string): boolean { return !!localStorage.getItem(`bid_prompt_${mode}`); },
    resetPrompt(mode: string) { localStorage.removeItem(`bid_prompt_${mode}`); },
    isSynced() { return isSynced; }
};
